import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { apiClient } from '@/lib/api-client';
import { useMe } from '@/hooks/auth/use-me';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [token, setToken] = React.useState<string | null>(null);

  // Extract token from query or hash
  React.useEffect(() => {
    let extractedToken = searchParams.get('accessToken') || searchParams.get('token');

    if (!extractedToken && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      extractedToken = hashParams.get('accessToken') || hashParams.get('token');
    }

    if (extractedToken) {
      setToken(extractedToken);
      // Temporarily store token in Zustand so that useMe axios requests will use it
      useAuthStore.setState({ accessToken: extractedToken });
    } else {
      navigate('/login?error=sso_failed');
    }
  }, [searchParams, navigate]);

  // Execute the useMe query once we have a token
  const { data: userContext, isSuccess, isError, error } = useMe({
    enabled: !!token,
  });

  React.useEffect(() => {
    if (isSuccess && userContext && token) {
      setAuth(userContext.user, userContext.tenant, token);
      navigate('/dashboard');
    }
  }, [isSuccess, userContext, token, setAuth, navigate]);

  React.useEffect(() => {
    if (isError) {
      console.error('Failed to resolve SSO user context:', error);
      useAuthStore.getState().clearAuth();
      navigate('/login?error=sso_context_failed');
    }
  }, [isError, error, navigate]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg-secondary p-6 text-center">
      <Card className="max-w-md w-full border border-border/50 shadow-xl bg-bg-elevated/80 backdrop-blur-md p-6">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center mb-3">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
          </div>
          <CardTitle className="text-2xl text-fg-primary">Completing Sign In</CardTitle>
          <CardDescription className="text-fg-secondary pt-1">
            Setting up your secure session workspace. Please hold on...
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-fg-muted">
          Establishing communication with your fabricator context...
        </CardContent>
      </Card>
    </div>
  );
}
